import fontMetrics from "@digital-twins/slide-schemas/generated/font-metrics.json";
import {
  CREATIVE_AD_FONTS,
  CREATIVE_AD_PLACEMENTS,
} from "@digital-twins/shared-types";
import type {
  AdImageBox,
  AdRenderRequest,
  AdRenderedExport,
} from "@/types/creative-layouts";

export class AdLayoutError extends Error {
  constructor(
    public readonly code:
      | "preset"
      | "font"
      | "image"
      | "overflow"
      | "bytes"
      | "canvas"
      | "input",
  ) {
    super(code);
  }
}

/** Measured wrapping preserves every supplied character, including explicit line breaks. */
export function wrapAdText(
  text: string,
  width: number,
  measure: (text: string) => number,
): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    if (!paragraph) {
      lines.push("");
      continue;
    }
    let line = "";
    for (const word of paragraph.match(/\S+\s*|\s+/g) ?? []) {
      if (line && measure(line + word) > width) {
        lines.push(line);
        line = "";
      }
      for (const character of Array.from(word)) {
        if (measure(character) > width) throw new AdLayoutError("overflow");
        if (line && measure(line + character) > width) {
          lines.push(line);
          line = "";
        }
        line += character;
      }
    }
    lines.push(line);
  }
  return lines;
}

export function imageBox(
  width: number,
  height: number,
  box: AdImageBox,
  fit: "contain" | "cover",
): AdImageBox {
  const ratio =
    fit === "contain"
      ? Math.min(box.width / width, box.height / height)
      : Math.max(box.width / width, box.height / height);
  return {
    x: box.x + (box.width - width * ratio) / 2,
    y: box.y + (box.height - height * ratio) / 2,
    width: width * ratio,
    height: height * ratio,
  };
}
const loadedFonts = new Map<string, Promise<void>>();
export function assertAdGlyphs(text: string, fontAsset: string) {
  const key = fontAsset.split("/").pop()!.replace(".woff2", "");
  const metrics = (
    fontMetrics as Record<string, { widths: Record<string, number> }>
  )[key];
  if (
    !metrics ||
    Array.from(text).some(
      (character) =>
        character !== "\n" &&
        !(String(character.codePointAt(0)) in metrics.widths),
    )
  )
    throw new AdLayoutError("font");
}
async function loadFont(font: (typeof CREATIVE_AD_FONTS)[number]) {
  let pending = loadedFonts.get(font.id);
  if (!pending) {
    pending = (async () => {
      const response = await fetch(font.asset);
      if (!response.ok) throw new Error("font unavailable");
      const bytes = await response.arrayBuffer();
      const digest = Array.from(
        new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)),
        (value) => value.toString(16).padStart(2, "0"),
      ).join("");
      if (digest !== font.sha256) throw new Error("font changed");
      const face = new FontFace(font.family, bytes);
      await face.load();
      document.fonts.add(face);
    })();
    loadedFonts.set(font.id, pending);
  }
  try {
    await pending;
  } catch {
    loadedFonts.delete(font.id);
    throw new AdLayoutError("font");
  }
}
async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    if (!image.naturalWidth || !image.naturalHeight) throw new Error("empty");
    return image;
  } catch {
    throw new AdLayoutError("image");
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function assertAdByteLimit(bytes: number, maximum: number | null) {
  if (bytes > (maximum ?? 8 * 1024 * 1024)) throw new AdLayoutError("bytes");
}

export async function renderAd({
  composition,
  sources,
}: AdRenderRequest): Promise<AdRenderedExport> {
  const spec = structuredClone(composition);
  const placement = CREATIVE_AD_PLACEMENTS.find(
    (item) => item.id === spec.placementId,
  );
  const font = CREATIVE_AD_FONTS.find((item) => item.id === spec.fontId);
  if (!placement) throw new AdLayoutError("preset");
  if (!font) throw new AdLayoutError("font");
  if (
    ![spec.background, spec.foreground].every((color) =>
      /^#[0-9a-f]{6}$/i.test(color),
    ) ||
    !["split", "footer"].includes(spec.template) ||
    !placement.mimeTypes.includes(spec.mimeType) ||
    spec.jpegQuality < 0.1 ||
    spec.jpegQuality > 1
  )
    throw new AdLayoutError("input");
  assertAdGlyphs([spec.headline, spec.body, spec.cta].join("\n"), font.asset);
  await loadFont(font);
  const image = await loadImage(sources.image);
  const logo = sources.logo ? await loadImage(sources.logo) : null;
  const canvas = document.createElement("canvas");
  canvas.width = placement.width;
  canvas.height = placement.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new AdLayoutError("canvas");
  const { width: w, height: h } = canvas;
  const inset = Math.min(w, h) * placement.contentInset;
  const photo: AdImageBox =
    spec.template === "split"
      ? { x: 0, y: 0, width: w * 0.55, height: h }
      : { x: 0, y: 0, width: w, height: h * 0.62 };
  const panel: AdImageBox =
    spec.template === "split"
      ? {
          x: photo.width + inset,
          y: inset,
          width: w - photo.width - 2 * inset,
          height: h - 2 * inset,
        }
      : {
          x: inset,
          y: photo.height + inset,
          width: w - 2 * inset,
          height: h - photo.height - 2 * inset,
        };
  ctx.fillStyle = spec.background;
  ctx.fillRect(0, 0, w, h);
  ctx.save();
  ctx.beginPath();
  ctx.rect(photo.x, photo.y, photo.width, photo.height);
  ctx.clip();
  const drawn = imageBox(
    image.naturalWidth,
    image.naturalHeight,
    photo,
    spec.fit,
  );
  ctx.drawImage(image, drawn.x, drawn.y, drawn.width, drawn.height);
  ctx.restore();
  let y = panel.y;
  if (logo) {
    const logoBox = imageBox(
      logo.naturalWidth,
      logo.naturalHeight,
      {
        x: panel.x,
        y,
        width: panel.width * 0.42,
        height: Math.min(h * 0.09, panel.height * 0.22),
      },
      "contain",
    );
    ctx.drawImage(logo, logoBox.x, logoBox.y, logoBox.width, logoBox.height);
    y += Math.min(h * 0.09, panel.height * 0.22) + inset * 0.5;
  }
  const base = Math.min(w, h);
  ctx.fillStyle = spec.foreground;
  ctx.textBaseline = "top";
  for (const [text, size] of [
    [spec.headline, Math.max(14, base * 0.055)],
    [spec.body, Math.max(10, base * 0.029)],
    [spec.cta, Math.max(11, base * 0.032)],
  ] as const) {
    if (!text) continue;
    ctx.font = `${size}px "${font.family}"`;
    const lines = wrapAdText(
      text,
      panel.width,
      (value) => ctx.measureText(value).width,
    );
    const lineHeight = size * 1.3;
    if (y + lines.length * lineHeight > panel.y + panel.height)
      throw new AdLayoutError("overflow");
    for (const line of lines) {
      ctx.fillText(line, panel.x, y);
      y += lineHeight;
    }
    y += inset * 0.45;
  }
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (value) => (value ? resolve(value) : reject(new AdLayoutError("canvas"))),
      spec.mimeType,
      spec.jpegQuality,
    ),
  );
  if (blob.type !== spec.mimeType) throw new AdLayoutError("canvas");
  assertAdByteLimit(blob.size, placement.maximumBytes);
  return {
    blob,
    manifest: {
      schemaVersion: 1,
      kind: "local_ad_export",
      composition: spec,
      placement: structuredClone(placement),
      font: structuredClone(font),
      output: { width: w, height: h, mimeType: blob.type, bytes: blob.size },
    },
  };
}
